import { useEffect, useMemo, useState } from 'react';
import type {
  BillingAuditAgreementLine,
  BillingAuditOverageCursor,
  BillingAuditTimeEntry,
  UpdateBillingAuditAgreementLineInput,
  UpdateBillingAuditOverageCursorInput,
  UpdateBillingAuditTimeEntryInput,
} from '@catalyst-psa/shared';
import { DEFAULT_PAGE_SIZE } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { formatDate, formatDateTime } from '../../utils/date.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { CheckboxField, TextField } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { ApiError } from '../../api/client.js';
import {
  useBillingAuditAgreementLines,
  useBillingAuditOverageCursors,
  useBillingAuditTimeEntries,
  useRefreshBillingAudit,
  useUpdateBillingAuditAgreementLine,
  useUpdateBillingAuditOverageCursor,
  useUpdateBillingAuditTimeEntry,
  type TimeEntryFilters,
} from './api.js';

type TabId = 'agreement-lines' | 'overage-cursors' | 'time-entries';

const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'agreement-lines', label: 'Agreement lines' },
  { id: 'overage-cursors', label: 'Overage cursors' },
  { id: 'time-entries', label: 'Billable time' },
];

function errorMessage(error: unknown, fallback: string): string | null {
  if (!error) return null;
  if (error instanceof ApiError) return error.message;
  return fallback;
}

function matches(cell: string | null, filter: string): boolean {
  if (!filter) return true;
  return (cell ?? '').toLowerCase().includes(filter.toLowerCase());
}


/** Adds whole days to a yyyy-mm-dd date string, returning yyyy-mm-dd (UTC, DST-safe). */
function addDaysIso(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return iso;
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

const FILTER_INPUT_CLASS =
  'w-full rounded border border-slate-300 px-2 py-1 text-xs text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

const BULK_INPUT_CLASS =
  'rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

interface BulkColumnDef {
  key: string;
  label: string;
  control: 'date' | 'datetime' | 'number' | 'text' | 'boolean';
}

const AGREEMENT_BULK_COLUMNS: BulkColumnDef[] = [
  { key: 'billedThroughDate', label: 'Billed through date', control: 'date' },
];

const OVERAGE_BULK_COLUMNS: BulkColumnDef[] = [
  { key: 'overageBilledThrough', label: 'Overage billed through', control: 'date' },
  { key: 'rolloverHours', label: 'Rollover hours', control: 'number' },
];

const TIME_ENTRY_BULK_COLUMNS: BulkColumnDef[] = [
  { key: 'billable', label: 'Billable', control: 'boolean' },
  { key: 'billedExternally', label: 'Billed externally', control: 'boolean' },
  { key: 'writtenOff', label: 'Written off', control: 'boolean' },
  { key: 'invoiceId', label: 'Invoice id', control: 'text' },
  { key: 'invoicedAt', label: 'Invoiced at', control: 'datetime' },
  { key: 'externalInvoiceRef', label: 'External invoice reference', control: 'text' },
];

interface RowSelection {
  selectedCount: number;
  allSelected: boolean;
  toggleAll: () => void;
  toggleRow: (id: string) => void;
  clear: () => void;
  isSelected: (id: string) => boolean;
}

function useRowSelection<T>(rows: T[], getId: (row: T) => string): RowSelection {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const ids = rows.map(getId);
  const selectedCount = ids.filter((id) => selected.has(id)).length;
  const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
  const toggleAll = (): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  const toggleRow = (id: string): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const clear = (): void => setSelected(new Set());
  const isSelected = (id: string): boolean => selected.has(id);
  return { selectedCount, allSelected, toggleAll, toggleRow, clear, isSelected };
}

interface BulkApply {
  applying: boolean;
  error: unknown;
  run: (tasks: Array<() => Promise<unknown>>) => Promise<boolean>;
}

function useBulkApply(): BulkApply {
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const run = async (tasks: Array<() => Promise<unknown>>): Promise<boolean> => {
    setApplying(true);
    setError(null);
    try {
      for (const task of tasks) {
        await task();
      }
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      setApplying(false);
    }
  };
  return { applying, error, run };
}

function BulkUpdateBar({
  columns,
  selectedCount,
  applying,
  error,
  onApply,
}: {
  columns: BulkColumnDef[];
  selectedCount: number;
  applying: boolean;
  error: unknown;
  onApply: (columnKey: string, value: string, setNull: boolean) => void;
}): JSX.Element {
  const [columnKey, setColumnKey] = useState(columns[0]?.key ?? '');
  const [value, setValue] = useState('');
  const [setNull, setSetNull] = useState(false);
  const column = columns.find((c) => c.key === columnKey) ?? columns[0];
  const isBoolean = column?.control === 'boolean';

  const handleColumnChange = (key: string): void => {
    setColumnKey(key);
    setValue('');
    setSetNull(false);
  };

  const inputType =
    column?.control === 'date'
      ? 'date'
      : column?.control === 'datetime'
        ? 'datetime-local'
        : column?.control === 'number'
          ? 'number'
          : 'text';

  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
        Column to update
        <select
          className={BULK_INPUT_CLASS}
          value={columnKey}
          onChange={(e) => handleColumnChange(e.target.value)}
        >
          {columns.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
        Value
        {isBoolean ? (
          <select
            className={BULK_INPUT_CLASS}
            value={value || 'true'}
            onChange={(e) => setValue(e.target.value)}
          >
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        ) : (
          <input
            className={BULK_INPUT_CLASS}
            type={inputType}
            step={column?.control === 'number' ? '0.01' : undefined}
            value={value}
            disabled={setNull}
            onChange={(e) => setValue(e.target.value)}
          />
        )}
      </label>

      {!isBoolean ? (
        <label className="flex items-center gap-2 pb-2 text-xs font-medium text-slate-600">
          <input type="checkbox" checked={setNull} onChange={(e) => setSetNull(e.target.checked)} />
          Set blank / null
        </label>
      ) : null}

      <Button
        type="button"
        onClick={() => onApply(columnKey, isBoolean ? value || 'true' : value, setNull)}
        disabled={selectedCount === 0 || applying}
        isLoading={applying}
      >
        Apply to {selectedCount} selected
      </Button>

      {errorMessage(error, 'Bulk update failed.') ? (
        <p className="w-full text-sm text-rose-600">{errorMessage(error, 'Bulk update failed.')}</p>
      ) : null}
    </div>
  );
}

export function BillingAuditPage(): JSX.Element {
  const { can } = useAuth();
  const canView = can('invoices', 'view');
  const [tab, setTab] = useState<TabId>('agreement-lines');
  const refresh = useRefreshBillingAudit();
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = (): void => {
    setRefreshing(true);
    void refresh().finally(() => setRefreshing(false));
  };

  if (!canView) {
    return (
      <div>
        <PageHeader title="Billing Audit" />
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-600 shadow-sm">
          You need invoice access to view the billing audit.
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Billing Audit"
        description="Inspect and hand-correct the raw billing-control data behind on-demand invoicing."
        actions={
          <Button type="button" variant="secondary" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
        }
      />

      <div className="mb-4 flex gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
              tab === t.id
                ? 'border-brand-500 text-brand-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'agreement-lines' ? <AgreementLinesSection /> : null}
      {tab === 'overage-cursors' ? <OverageCursorsSection /> : null}
      {tab === 'time-entries' ? <TimeEntriesSection /> : null}
    </div>
  );
}

// --- Agreement lines -------------------------------------------------------

function AgreementLinesSection(): JSX.Element {
  const query = useBillingAuditAgreementLines();
  const update = useUpdateBillingAuditAgreementLine();
  const bulk = useBulkApply();
  const [editing, setEditing] = useState<BillingAuditAgreementLine | null>(null);
  const [billedThrough, setBilledThrough] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});

  const rows = query.data ?? [];
  const filtered = useMemo(
    () =>
      rows.filter(
        (r) =>
          matches(`${r.agreementNumber} ${r.agreementName}`, filters.agreement ?? '') &&
          matches(r.customerName, filters.customer ?? '') &&
          matches(r.productName, filters.product ?? '') &&
          matches(r.description, filters.description ?? '') &&
          matches(r.billingFrequency, filters.frequency ?? '') &&
          matches(r.billedThroughDate, filters.billedThrough ?? '') &&
          matches(r.nextBillableDate, filters.nextBillable ?? '') &&
          matches(r.source, filters.source ?? ''),
      ),
    [rows, filters],
  );

  const setFilter = (key: string, value: string): void =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const selection = useRowSelection(filtered, (r) => r.id);

  const handleBulkApply = (_columnKey: string, value: string, setNull: boolean): void => {
    const input: UpdateBillingAuditAgreementLineInput = {
      billedThroughDate: setNull || !value ? null : value,
    };
    void bulk
      .run(
        filtered
          .filter((r) => selection.isSelected(r.id))
          .map((r) => () => update.mutateAsync({ id: r.id, input })),
      )
      .then((ok) => {
        if (ok) selection.clear();
      });
  };

  const openEdit = (row: BillingAuditAgreementLine): void => {
    setEditing(row);
    setBilledThrough(row.billedThroughDate ?? '');
  };

  const handleSave = (): void => {
    if (!editing) return;
    update.mutate(
      { id: editing.id, input: { billedThroughDate: billedThrough ? billedThrough : null } },
      { onSuccess: () => setEditing(null) },
    );
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-end gap-3 border-b border-slate-200 bg-slate-50 p-3">
        <BulkUpdateBar
          columns={AGREEMENT_BULK_COLUMNS}
          selectedCount={selection.selectedCount}
          applying={bulk.applying}
          error={bulk.error}
          onApply={handleBulkApply}
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">
                <input
                  type="checkbox"
                  checked={selection.allSelected}
                  onChange={selection.toggleAll}
                />
              </th>
              <th className="px-3 py-2 font-medium">Agreement</th>
              <th className="px-3 py-2 font-medium">Customer</th>
              <th className="px-3 py-2 font-medium">Product</th>
              <th className="px-3 py-2 font-medium">Description</th>
              <th className="px-3 py-2 font-medium">Freq.</th>
              <th className="px-3 py-2 font-medium">Qty</th>
              <th className="px-3 py-2 font-medium">Unit price</th>
              <th className="px-3 py-2 font-medium">Start</th>
              <th className="px-3 py-2 font-medium">Billed through</th>
              <th className="px-3 py-2 font-medium">Next billable</th>
              <th className="px-3 py-2 font-medium">Source</th>
            </tr>
            <tr>
              <th />
              <th className="px-3 pb-2">
                <input
                  className={FILTER_INPUT_CLASS}
                  placeholder="Filter"
                  value={filters.agreement ?? ''}
                  onChange={(e) => setFilter('agreement', e.target.value)}
                />
              </th>
              <th className="px-3 pb-2">
                <input
                  className={FILTER_INPUT_CLASS}
                  placeholder="Filter"
                  value={filters.customer ?? ''}
                  onChange={(e) => setFilter('customer', e.target.value)}
                />
              </th>
              <th className="px-3 pb-2">
                <input
                  className={FILTER_INPUT_CLASS}
                  placeholder="Filter"
                  value={filters.product ?? ''}
                  onChange={(e) => setFilter('product', e.target.value)}
                />
              </th>
              <th className="px-3 pb-2">
                <input
                  className={FILTER_INPUT_CLASS}
                  placeholder="Filter"
                  value={filters.description ?? ''}
                  onChange={(e) => setFilter('description', e.target.value)}
                />
              </th>
              <th className="px-3 pb-2">
                <input
                  className={FILTER_INPUT_CLASS}
                  placeholder="Filter"
                  value={filters.frequency ?? ''}
                  onChange={(e) => setFilter('frequency', e.target.value)}
                />
              </th>
              <th />
              <th />
              <th />
              <th className="px-3 pb-2">
                <input
                  className={FILTER_INPUT_CLASS}
                  placeholder="Filter"
                  value={filters.billedThrough ?? ''}
                  onChange={(e) => setFilter('billedThrough', e.target.value)}
                />
              </th>
              <th className="px-3 pb-2">
                <input
                  className={FILTER_INPUT_CLASS}
                  placeholder="Filter"
                  value={filters.nextBillable ?? ''}
                  onChange={(e) => setFilter('nextBillable', e.target.value)}
                />
              </th>
              <th className="px-3 pb-2">
                <input
                  className={FILTER_INPUT_CLASS}
                  placeholder="Filter"
                  value={filters.source ?? ''}
                  onChange={(e) => setFilter('source', e.target.value)}
                />
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={12}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={filtered.length === 0}
              emptyTitle="No agreement lines"
              emptyDescription="No recurring agreement lines match the current filters."
            >
              {filtered.map((row) => (
                <tr
                  key={row.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => openEdit(row)}
                >
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selection.isSelected(row.id)}
                      onChange={() => selection.toggleRow(row.id)}
                    />
                  </td>
                  <td className="px-3 py-2 font-medium text-slate-900">
                    #{row.agreementNumber} {row.agreementName}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{row.customerName}</td>
                  <td className="px-3 py-2 text-slate-600">{row.productName ?? '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{row.description}</td>
                  <td className="px-3 py-2 text-slate-600">{row.billingFrequency}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-600">{row.quantity}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-600">{row.unitPrice}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-600">{formatDate(row.startDate)}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-900">
                    {formatDate(row.billedThroughDate)}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-slate-900">{formatDate(row.nextBillableDate)}</td>
                  <td className="px-3 py-2">
                    <Badge tone={row.source === 'connectwise' ? 'blue' : 'gray'}>{row.source}</Badge>
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={editing !== null}
        onClose={() => setEditing(null)}
        title="Edit agreement line cursor"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={handleSave} isLoading={update.isPending}>
              Save
            </Button>
          </div>
        }
      >
        {editing ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              #{editing.agreementNumber} {editing.agreementName} — {editing.description}
            </p>
            <TextField
              label="Billed through date"
              type="date"
              value={billedThrough}
              onChange={(e) => setBilledThrough(e.target.value)}
            />
            <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <span className="font-medium text-slate-700">Next billable: </span>
              {formatDate(billedThrough ? addDaysIso(billedThrough, 1) : editing.startDate)}
              <p className="mt-1 text-slate-500">
                This is the date the line can next be invoiced — the cursor that prevents it from
                billing more than once per cycle. The engine bills in advance and sets “Billed
                through” to the end of the cycle it just billed, so a future date here means the
                current cycle is already billed and won’t bill again until then.
              </p>
            </div>
            <p className="text-xs text-slate-500">
              Clear the date to mark the line as never billed (its full history becomes billable
              again).
            </p>
            {errorMessage(update.error, 'Failed to save.') ? (
              <p className="text-sm text-rose-600">
                {errorMessage(update.error, 'Failed to save.')}
              </p>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

// --- Overage cursors -------------------------------------------------------

function OverageCursorsSection(): JSX.Element {
  const query = useBillingAuditOverageCursors();
  const update = useUpdateBillingAuditOverageCursor();
  const bulk = useBulkApply();
  const [editing, setEditing] = useState<BillingAuditOverageCursor | null>(null);
  const [overageBilledThrough, setOverageBilledThrough] = useState('');
  const [rolloverHours, setRolloverHours] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});

  const rows = query.data ?? [];
  const filtered = useMemo(
    () =>
      rows.filter(
        (r) =>
          matches(`${r.agreementNumber} ${r.agreementName}`, filters.agreement ?? '') &&
          matches(r.customerName, filters.customer ?? '') &&
          matches(r.overageBilledThrough, filters.overage ?? ''),
      ),
    [rows, filters],
  );

  const setFilter = (key: string, value: string): void =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const selection = useRowSelection(filtered, (r) => r.agreementId);

  const handleBulkApply = (columnKey: string, value: string, setNull: boolean): void => {
    const input: UpdateBillingAuditOverageCursorInput =
      columnKey === 'rolloverHours'
        ? { rolloverHours: setNull || !value.trim() ? null : value.trim() }
        : { overageBilledThrough: setNull || !value ? null : value };
    void bulk
      .run(
        filtered
          .filter((r) => selection.isSelected(r.agreementId))
          .map((r) => () => update.mutateAsync({ agreementId: r.agreementId, input })),
      )
      .then((ok) => {
        if (ok) selection.clear();
      });
  };

  const openEdit = (row: BillingAuditOverageCursor): void => {
    setEditing(row);
    setOverageBilledThrough(row.overageBilledThrough ?? '');
    setRolloverHours(row.rolloverHours ?? '');
  };

  const handleSave = (): void => {
    if (!editing) return;
    update.mutate(
      {
        agreementId: editing.agreementId,
        input: {
          overageBilledThrough: overageBilledThrough ? overageBilledThrough : null,
          rolloverHours: rolloverHours.trim() ? rolloverHours.trim() : null,
        },
      },
      { onSuccess: () => setEditing(null) },
    );
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-end gap-3 border-b border-slate-200 bg-slate-50 p-3">
        <BulkUpdateBar
          columns={OVERAGE_BULK_COLUMNS}
          selectedCount={selection.selectedCount}
          applying={bulk.applying}
          error={bulk.error}
          onApply={handleBulkApply}
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">
                <input
                  type="checkbox"
                  checked={selection.allSelected}
                  onChange={selection.toggleAll}
                />
              </th>
              <th className="px-3 py-2 font-medium">Agreement</th>
              <th className="px-3 py-2 font-medium">Customer</th>
              <th className="px-3 py-2 font-medium">Hours incl.</th>
              <th className="px-3 py-2 font-medium">Rollover?</th>
              <th className="px-3 py-2 font-medium">Cap</th>
              <th className="px-3 py-2 font-medium">Rate</th>
              <th className="px-3 py-2 font-medium">Overage billed through</th>
              <th className="px-3 py-2 font-medium">Rollover hours</th>
            </tr>
            <tr>
              <th />
              <th className="px-3 pb-2">
                <input
                  className={FILTER_INPUT_CLASS}
                  placeholder="Filter"
                  value={filters.agreement ?? ''}
                  onChange={(e) => setFilter('agreement', e.target.value)}
                />
              </th>
              <th className="px-3 pb-2">
                <input
                  className={FILTER_INPUT_CLASS}
                  placeholder="Filter"
                  value={filters.customer ?? ''}
                  onChange={(e) => setFilter('customer', e.target.value)}
                />
              </th>
              <th />
              <th />
              <th />
              <th />
              <th className="px-3 pb-2">
                <input
                  className={FILTER_INPUT_CLASS}
                  placeholder="Filter"
                  value={filters.overage ?? ''}
                  onChange={(e) => setFilter('overage', e.target.value)}
                />
              </th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={9}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={filtered.length === 0}
              emptyTitle="No managed-services agreements"
              emptyDescription="No managed-services agreements match the current filters."
            >
              {filtered.map((row) => (
                <tr
                  key={row.agreementId}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => openEdit(row)}
                >
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selection.isSelected(row.agreementId)}
                      onChange={() => selection.toggleRow(row.agreementId)}
                    />
                  </td>
                  <td className="px-3 py-2 font-medium text-slate-900">
                    #{row.agreementNumber} {row.agreementName}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{row.customerName}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-600">
                    {row.hoursIncluded ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{row.allowRollover ? 'Yes' : 'No'}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-600">{row.hoursCap ?? '—'}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-600">
                    {row.overageRate ?? '—'}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-slate-900">
                    {formatDate(row.overageBilledThrough)}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-slate-900">
                    {row.rolloverHours ?? '—'}
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={editing !== null}
        onClose={() => setEditing(null)}
        title="Edit overage cursor"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={handleSave} isLoading={update.isPending}>
              Save
            </Button>
          </div>
        }
      >
        {editing ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              #{editing.agreementNumber} {editing.agreementName}
            </p>
            <TextField
              label="Overage billed through"
              type="date"
              value={overageBilledThrough}
              onChange={(e) => setOverageBilledThrough(e.target.value)}
            />
            <TextField
              label="Rollover hours"
              type="number"
              step="0.01"
              value={rolloverHours}
              onChange={(e) => setRolloverHours(e.target.value)}
            />
            {errorMessage(update.error, 'Failed to save.') ? (
              <p className="text-sm text-rose-600">
                {errorMessage(update.error, 'Failed to save.')}
              </p>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

// --- Time entries ----------------------------------------------------------

function TimeEntriesSection(): JSX.Element {
  const [page, setPage] = useState(1);
  const [serverFilters, setServerFilters] = useState<TimeEntryFilters>({});
  const query = useBillingAuditTimeEntries(page, DEFAULT_PAGE_SIZE, serverFilters);
  const update = useUpdateBillingAuditTimeEntry();
  const bulk = useBulkApply();
  const [editing, setEditing] = useState<BillingAuditTimeEntry | null>(null);
  const [billable, setBillable] = useState(false);
  const [billedExternally, setBilledExternally] = useState(false);
  const [writtenOff, setWrittenOff] = useState(false);
  const [invoiceId, setInvoiceId] = useState('');
  const [invoicedAt, setInvoicedAt] = useState('');
  const [externalRef, setExternalRef] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});

  // Server filters narrow the full result set (the list is paginated, so client-side
  // text filters below only refine the current page). Reset to page 1 on any change.
  const setServerFilter = (key: keyof TimeEntryFilters, value: string): void => {
    setPage(1);
    setServerFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Text filters (member/customer/agreement/project/ticket) search ALL entries
  // server-side, so they must reach the API — but debounced so typing doesn't fire a
  // request per keystroke. The invoice-number filter stays client-side (derived value).
  useEffect(() => {
    const handle = setTimeout(() => {
      setPage(1);
      setServerFilters((prev) => ({
        ...prev,
        member: filters.member ?? '',
        customer: filters.customer ?? '',
        agreement: filters.agreement ?? '',
        project: filters.project ?? '',
        ticket: filters.ticket ?? '',
        cwId: filters.cwId ?? '',
      }));
    }, 300);
    return () => clearTimeout(handle);
  }, [filters.member, filters.customer, filters.agreement, filters.project, filters.ticket, filters.cwId]);

  const rows = query.data?.data ?? [];
  const pageInfo = query.data?.page;
  // Member/customer/agreement/project/ticket filter server-side (see serverFilters), so
  // they search ALL entries. Only the invoice-number filter stays client-side (the
  // invoice number is derived, not a queryable column) and refines the current page.
  const filtered = useMemo(
    () => rows.filter((r) => matches(r.invoiceNumber, filters.invoice ?? '')),
    [rows, filters],
  );

  const setFilter = (key: string, value: string): void =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const selection = useRowSelection(filtered, (r) => r.id);

  const handleBulkApply = (columnKey: string, value: string, setNull: boolean): void => {
    let input: UpdateBillingAuditTimeEntryInput;
    switch (columnKey) {
      case 'billable':
        input = { billable: value === 'true' };
        break;
      case 'billedExternally':
        input = { billedExternally: value === 'true' };
        break;
      case 'writtenOff':
        input = { writtenOff: value === 'true' };
        break;
      case 'invoiceId':
        input = { invoiceId: setNull || !value.trim() ? null : value.trim() };
        break;
      case 'invoicedAt':
        input = { invoicedAt: setNull || !value ? null : new Date(value).toISOString() };
        break;
      default:
        input = { externalInvoiceRef: setNull || !value.trim() ? null : value.trim() };
    }
    void bulk
      .run(
        filtered
          .filter((r) => selection.isSelected(r.id))
          .map((r) => () => update.mutateAsync({ id: r.id, input })),
      )
      .then((ok) => {
        if (ok) selection.clear();
      });
  };

  const openEdit = (row: BillingAuditTimeEntry): void => {
    setEditing(row);
    setBillable(row.billable);
    setBilledExternally(row.billedExternally);
    setWrittenOff(row.writtenOff);
    setInvoiceId(row.invoiceId ?? '');
    setInvoicedAt(row.invoicedAt ? row.invoicedAt.slice(0, 16) : '');
    setExternalRef(row.externalInvoiceRef ?? '');
  };

  const handleSave = (): void => {
    if (!editing) return;
    update.mutate(
      {
        id: editing.id,
        input: {
          billable,
          billedExternally,
          writtenOff,
          invoiceId: invoiceId.trim() ? invoiceId.trim() : null,
          invoicedAt: invoicedAt ? new Date(invoicedAt).toISOString() : null,
          externalInvoiceRef: externalRef.trim() ? externalRef.trim() : null,
        },
      },
      { onSuccess: () => setEditing(null) },
    );
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-end gap-3 border-b border-slate-200 bg-slate-50 p-3">
        <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
          Work date from
          <input
            type="date"
            className={BULK_INPUT_CLASS}
            value={serverFilters.workDateFrom ?? ''}
            onChange={(e) => setServerFilter('workDateFrom', e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
          Work date to
          <input
            type="date"
            className={BULK_INPUT_CLASS}
            value={serverFilters.workDateTo ?? ''}
            onChange={(e) => setServerFilter('workDateTo', e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
          Billable
          <select
            className={BULK_INPUT_CLASS}
            value={serverFilters.billable ?? ''}
            onChange={(e) => setServerFilter('billable', e.target.value)}
          >
            <option value="">All</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
          Invoiced
          <select
            className={BULK_INPUT_CLASS}
            value={serverFilters.invoiced ?? ''}
            onChange={(e) => setServerFilter('invoiced', e.target.value)}
          >
            <option value="">All</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
          Billed ext.
          <select
            className={BULK_INPUT_CLASS}
            value={serverFilters.billedExternally ?? ''}
            onChange={(e) => setServerFilter('billedExternally', e.target.value)}
          >
            <option value="">All</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
          Written off
          <select
            className={BULK_INPUT_CLASS}
            value={serverFilters.writtenOff ?? ''}
            onChange={(e) => setServerFilter('writtenOff', e.target.value)}
          >
            <option value="">All</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </label>
      </div>
      <div className="flex flex-wrap items-end gap-3 border-b border-slate-200 bg-slate-50 p-3">
        <BulkUpdateBar
          columns={TIME_ENTRY_BULK_COLUMNS}
          selectedCount={selection.selectedCount}
          applying={bulk.applying}
          error={bulk.error}
          onApply={handleBulkApply}
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">
                <input
                  type="checkbox"
                  checked={selection.allSelected}
                  onChange={selection.toggleAll}
                />
              </th>
              <th className="px-3 py-2 font-medium">Work date</th>
              <th className="px-3 py-2 font-medium">Member</th>
              <th className="px-3 py-2 font-medium">Customer</th>
              <th className="px-3 py-2 font-medium">Agreement</th>
              <th className="px-3 py-2 font-medium">Project</th>
              <th className="px-3 py-2 font-medium">Ticket</th>
              <th className="px-3 py-2 font-medium">CW ID</th>
              <th className="px-3 py-2 font-medium">Hours</th>
              <th className="px-3 py-2 font-medium">Rate</th>
              <th className="px-3 py-2 font-medium">Billable</th>
              <th className="px-3 py-2 font-medium">Invoice</th>
              <th className="px-3 py-2 font-medium">Invoiced</th>
              <th className="px-3 py-2 font-medium">Billed ext.</th>
              <th className="px-3 py-2 font-medium">Written off</th>
            </tr>
            <tr>
              <th />
              <th />
              <th className="px-3 pb-2">
                <input
                  className={FILTER_INPUT_CLASS}
                  placeholder="Filter"
                  value={filters.member ?? ''}
                  onChange={(e) => setFilter('member', e.target.value)}
                />
              </th>
              <th className="px-3 pb-2">
                <input
                  className={FILTER_INPUT_CLASS}
                  placeholder="Filter"
                  value={filters.customer ?? ''}
                  onChange={(e) => setFilter('customer', e.target.value)}
                />
              </th>
              <th className="px-3 pb-2">
                <input
                  className={FILTER_INPUT_CLASS}
                  placeholder="Filter"
                  value={filters.agreement ?? ''}
                  onChange={(e) => setFilter('agreement', e.target.value)}
                />
              </th>
              <th className="px-3 pb-2">
                <input
                  className={FILTER_INPUT_CLASS}
                  placeholder="Filter"
                  value={filters.project ?? ''}
                  onChange={(e) => setFilter('project', e.target.value)}
                />
              </th>
              <th className="px-3 pb-2">
                <input
                  className={FILTER_INPUT_CLASS}
                  placeholder="Filter"
                  value={filters.ticket ?? ''}
                  onChange={(e) => setFilter('ticket', e.target.value)}
                />
              </th>
              <th className="px-3 pb-2">
                <input
                  className={FILTER_INPUT_CLASS}
                  placeholder="Filter"
                  value={filters.cwId ?? ''}
                  onChange={(e) => setFilter('cwId', e.target.value)}
                />
              </th>
              <th />
              <th />
              <th />
              <th className="px-3 pb-2">
                <input
                  className={FILTER_INPUT_CLASS}
                  placeholder="Filter"
                  value={filters.invoice ?? ''}
                  onChange={(e) => setFilter('invoice', e.target.value)}
                />
              </th>
              <th />
              <th />
              <th />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={15}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={filtered.length === 0}
              emptyTitle="No time entries"
              emptyDescription="No time entries match the current filters."
            >
              {filtered.map((row) => (
                <tr
                  key={row.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => openEdit(row)}
                >
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selection.isSelected(row.id)}
                      onChange={() => selection.toggleRow(row.id)}
                    />
                  </td>
                  <td className="px-3 py-2 tabular-nums text-slate-600">{formatDate(row.workDate)}</td>
                  <td className="px-3 py-2 text-slate-900">{row.memberName}</td>
                  <td className="px-3 py-2 text-slate-600">{row.customerName ?? '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{row.agreementName ?? '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{row.projectName ?? '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{row.ticketTitle ?? '—'}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-600">{row.cwId ?? '—'}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-600">{row.hours}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-600">{row.resolvedRate}</td>
                  <td className="px-3 py-2">
                    <Badge tone={row.billable ? 'green' : 'gray'}>
                      {row.billable ? 'Yes' : 'No'}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{row.invoiceNumber ?? '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{formatDateTime(row.invoicedAt)}</td>
                  <td className="px-3 py-2">
                    {row.billedExternally ? <Badge tone="amber">External</Badge> : '—'}
                  </td>
                  <td className="px-3 py-2">
                    {row.writtenOff ? <Badge tone="red">Written off</Badge> : '—'}
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>
      </div>

      {pageInfo && rows.length > 0 ? (
        <Pagination
          page={pageInfo.page}
          pageSize={pageInfo.pageSize}
          totalItems={pageInfo.totalItems}
          totalPages={pageInfo.totalPages}
          onPageChange={setPage}
        />
      ) : null}

      <Modal
        isOpen={editing !== null}
        onClose={() => setEditing(null)}
        title="Edit time entry billing"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={handleSave} isLoading={update.isPending}>
              Save
            </Button>
          </div>
        }
      >
        {editing ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              {formatDate(editing.workDate)} — {editing.memberName} — {editing.hours} hrs @ {editing.resolvedRate}
            </p>
            <CheckboxField
              label="Billable"
              checked={billable}
              onChange={(e) => setBillable(e.target.checked)}
            />
            <CheckboxField
              label="Billed externally (excluded from invoice previews)"
              checked={billedExternally}
              onChange={(e) => setBilledExternally(e.target.checked)}
            />
            <CheckboxField
              label="Written off (excluded from all invoices)"
              checked={writtenOff}
              onChange={(e) => setWrittenOff(e.target.checked)}
            />
            <TextField
              label="Invoice id"
              placeholder="UUID, or blank to un-link"
              value={invoiceId}
              onChange={(e) => setInvoiceId(e.target.value)}
            />
            <TextField
              label="Invoiced at"
              type="datetime-local"
              value={invoicedAt}
              onChange={(e) => setInvoicedAt(e.target.value)}
            />
            <TextField
              label="External invoice reference"
              value={externalRef}
              onChange={(e) => setExternalRef(e.target.value)}
            />
            {errorMessage(update.error, 'Failed to save.') ? (
              <p className="text-sm text-rose-600">
                {errorMessage(update.error, 'Failed to save.')}
              </p>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
